import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHourlyNetFlow, HourlyNetFlowData } from '../../api/metrics';
import './HourlyNetFlowHeatmap.css';

interface HourlyNetFlowHeatmapProps {
  selectedDate?: string;
  productId?: string;
}

const HourlyNetFlowHeatmap: React.FC<HourlyNetFlowHeatmapProps> = ({ selectedDate, productId }) => {
  // Get last 7 days of hourly net flow data
  const getDates = () => {
    const dates = [];
    const today = selectedDate || new Date().toISOString().split('T')[0];
    const [year, month, dayOfMonth] = today.split('-').map(Number);
    const baseDate = new Date(year, month - 1, dayOfMonth);

    for (let i = 6; i >= 0; i--) {
      const date = new Date(baseDate);
      date.setDate(date.getDate() - i);
      const dateStr = date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0');
      dates.push(dateStr);
    }
    return dates;
  };

  const dates = getDates();
  const endDate = dates[dates.length - 1];

  // Fetch hourly net flow data for the last 7 days
  const { data, isLoading, isError } = useQuery({
    queryKey: ['hourly-net-flow', endDate, productId],
    queryFn: () => getHourlyNetFlow(7, endDate, productId),
    staleTime: 60000,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="netflow-heatmap-container">
        <div className="netflow-heatmap-loading">Loading hourly net flow data...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="netflow-heatmap-container">
        <div className="netflow-heatmap-error">Unable to load hourly net flow data</div>
      </div>
    );
  }

  // Process hourly data into a 7x24 grid
  const hourlyFlowGrid: { [dateKey: string]: { [hour: number]: number } } = {};
  let minNetFlow = 0;
  let maxNetFlow = 0;
  let hasAnyData = false;

  // Initialize grid with zeros
  dates.forEach((date) => {
    hourlyFlowGrid[date] = {};
    for (let hour = 0; hour < 24; hour++) {
      hourlyFlowGrid[date][hour] = 0;
    }
  });

  // Populate grid with actual data
  if (data && Array.isArray(data.hourlyData)) {
    data.hourlyData.forEach((hourData: HourlyNetFlowData) => {
      const netFlow = hourData.netFlow || 0;

      if (hourlyFlowGrid[hourData.date]) {
        hourlyFlowGrid[hourData.date][hourData.hour] = netFlow;
      }

      if (netFlow !== 0) {
        hasAnyData = true;
        minNetFlow = Math.min(minNetFlow, netFlow);
        maxNetFlow = Math.max(maxNetFlow, netFlow);
      }
    });
  }

  // Calculate color intensity based on net flow
  const getColor = (netFlow: number) => {
    if (netFlow === 0 || !hasAnyData) return '#1e293b';

    const maxAbsFlow = Math.max(Math.abs(minNetFlow), Math.abs(maxNetFlow));

    if (netFlow > 0) {
      // Positive net flow = more sells than buys = GREEN
      const intensity = Math.min(netFlow / maxAbsFlow, 1);
      const red = Math.round(209 - (209 - 22) * intensity);
      const green = Math.round(250 - (250 - 163) * intensity);
      const blue = Math.round(229 - (229 - 74) * intensity);
      return `rgb(${red}, ${green}, ${blue})`;
    } else {
      // Negative net flow = more buys than sells = RED
      const intensity = Math.min(Math.abs(netFlow) / maxAbsFlow, 1);
      const red = Math.round(254 - (254 - 239) * intensity);
      const green = Math.round(200 - (200 - 68) * intensity);
      const blue = Math.round(150 - (150 - 68) * intensity);
      return `rgb(${red}, ${green}, ${blue})`;
    }
  };

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${period}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getTooltipText = (netFlow: number, hour: number, date: string) => {
    const formattedDate = new Date(date + 'T00:00:00').toLocaleDateString();
    const hourLabel = formatHour(hour);

    if (netFlow === 0) {
      return `${hourLabel} on ${formattedDate}: No trades`;
    }

    const flowType = netFlow > 0 ? 'Net Selling' : 'Net Buying';
    return `${hourLabel} on ${formattedDate}: ${flowType} ${formatCurrency(Math.abs(netFlow))}`;
  };

  return (
    <div className="netflow-heatmap-container">
      <table className="netflow-heatmap-table">
        <thead>
          <tr>
            <th className="netflow-corner-cell"></th>
            {dates.map((date) => (
              <th key={date} className="netflow-date-header">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, hour) => (
            <tr key={hour}>
              <td className="netflow-hour-label">{formatHour(hour)}</td>
              {dates.map((date) => {
                const netFlow = hourlyFlowGrid[date]?.[hour] || 0;
                return (
                  <td
                    key={date}
                    className="netflow-cell"
                    style={{ backgroundColor: getColor(netFlow) }}
                    title={getTooltipText(netFlow, hour, date)}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {hasAnyData && (
        <div className="netflow-heatmap-legend">
          <div className="netflow-legend-item">
            <div className="netflow-legend-color netflow-legend-buying"></div>
            <span>Net Buying</span>
          </div>
          <div className="netflow-legend-item">
            <div className="netflow-legend-color netflow-legend-neutral"></div>
            <span>No Activity</span>
          </div>
          <div className="netflow-legend-item">
            <div className="netflow-legend-color netflow-legend-selling"></div>
            <span>Net Selling</span>
          </div>
        </div>
      )}

      {!hasAnyData && (
        <div className="netflow-heatmap-empty">No hourly net flow data available for the selected period</div>
      )}
    </div>
  );
};

export default HourlyNetFlowHeatmap;

